import { useTheme } from '@mui/material';
import {
  AwsIcon,
  WebIcon,
  MobileIcon,
  DefiIcon,
  FintechIcon,
  BlockchainIcon,
  TeamIcon,
  DesignIcon,
  StartupIcon,
  AIIcon,
  SyntheticDataGenerationIcon,
  ZeroKnowledgeProofIcon,
  XRPLIcon,
  StellarIcon,
  SmartContractIcon,
  AIKnowledgeManagementSolutionsIcon,
} from '@rumblefish/ui/Rumblefish23Theme';
import type { IAccordionData } from '@rumblefish/ui/src/components/Rumblefish23Theme/Accordion/CustomizedAccordion.types';

export const whatWeDoData = {
  mainText: 'Software Development Services and Skills for your needs',
  description: `  To deliver the highest quality of services, our experts are always
  gaining new skills and knowledge. That’s how we make sure our
  solutions follow the latest industry standards and take advantage
  of the most innovative technologies.`,
};

export const AccordionData = (): IAccordionData[] => {
  const theme = useTheme();
  return [
    {
      title: 'Blockchain Software Development',
      description:
        'Our team is well-versed and experienced in various blockchain development tools and technologies. Our unique skillset allows us to be at the forefront of Web3 development services so if you’re looking for a trusted IT partner to boost your decentralized product - look no further!',
      linkTo: '/services/blockchain-software-development/',
      icon: BlockchainIcon,
      gradientOnFocus: theme.mixins.cyanGradient?.background as string,
    },
    {
      title: 'Zero-Knowledge Proof Development',
      description:
        'We deliver production-ready zero-knowledge proof solutions that actually ship to mainnet, specializing in custom ZK development, rollup scaling solutions, and privacy-preserving smart contracts that reduce processing times from hours to minutes. Try us!',
      linkTo: '/services/zero-knowledge-proof-development/',
      icon: ZeroKnowledgeProofIcon,
      gradientOnFocus: theme.mixins.tealGradient?.background as string,
    },
    {
      title: 'XRPL Blockchain Development',
      description:
        'We build fast, compliant, and cost-effective blockchain solutions on the XRP Ledger. From payment systems and tokenization platforms to enterprise DeFi applications, our team delivers production-ready systems that work when billions are on the line.',
      linkTo: '/services/xrpl-blockchain-development/',
      icon: XRPLIcon,
      gradientOnFocus: theme.mixins.blueGradient?.background as string,
    },
    {
      title: 'Stellar Product Development',
      description:
        'Rumble Fish builds Stellar and Soroban software for startups, enterprises, and institutions. We develop payment rails, tokenization platforms, smart contracts, and DeFi applications that draw on Stellar\'s speed, low costs, and financial infrastructure.',
      linkTo: '/services/stellar-product-development/',
      icon: StellarIcon,
      gradientOnFocus: theme.mixins.violetGradient?.background as string,
    },
    {
      title: 'Smart Contract Development',
      description:
        'We build smart contracts that handle real business complexity without the usual blockchain headaches. From DeFi protocols to custom on-chain systems, we deliver production-ready solutions that scale.',
      linkTo: '/services/smart-contract-development/',
      icon: SmartContractIcon,
      gradientOnFocus: theme.mixins.emeraldGradient?.background as string,
    },
    {
      title: 'DeFi Development',
      description:
        'Decentralized Finance (DeFi) development requires an extensive amount of blockchain knowledge, as well as a great understanding of financial mechanisms. We’ve got both that bases covered! Our team has successfully built an impressive number of DeFi products like cryptocurrency exchanges, dApps, lending protocols, or staking platforms. Try us!',
      linkTo: '/services/defi-development/',
      icon: DefiIcon,
      gradientOnFocus: theme.mixins.grapeGradient?.background as string,
    },
    {
      title: 'AWS Cloud Solutions',
      description:
        'Our experienced team will take your AWS cloud solutions to the next level. AWS provides purpose-built tools to support your needs, and it is the preferred choice for any blockchain project. From the plethora of cloud tools and solutions offered by Amazon Web Services, we’ll help you choose and implement the ones that serve your business the best way possible.',
      linkTo: '/services/aws-cloud-solutions/',
      icon: AwsIcon,
      gradientOnFocus: theme.mixins.redGradient?.background as string,
    },
    {
      title: 'AI Chat Assistant Development',
      description: `AI chatbots can bring value to a wide range of industries by enhancing customer interactions, streamlining processes, and improving overall efficiency. We'll craft a perfect AI assistant for your product.`,
      linkTo: '/services/ai-chat-assistant/',
      icon: AIIcon,
      gradientOnFocus: theme.mixins.purpleGradient?.background as string,
    },
    {
      title: 'Synthetic Data Generation',
      description: `Need realistic data for AI training, testing, or product development—but privacy, scale, or availability is blocking you? We engineer custom synthetic data solutions that capture the complexity of real-world data without the constraints. From multi-modal generation to domain-specific datasets, we build what platforms can't deliver.`,
      linkTo: '/services/synthetic-data-generation/',
      icon: SyntheticDataGenerationIcon,
      gradientOnFocus: theme.mixins.aquamarineGradient?.background as string,
    },
    {
      title: 'AI Knowledge Management Solutions',
      description: `We build custom AI knowledge management systems that turn your scattered enterprise knowledge into instant, accurate answers - no more employees wasting their valuable time hunting through SharePoint and Slack for information. Unlike platforms that trap you in subscriptions, we engineer RAG solutions specifically for your data and security requirements, then hand you complete ownership of the source code and infrastructure.`,
      linkTo: '/services/ai-knowledge-management-solutions/',
      icon: AIKnowledgeManagementSolutionsIcon,
      gradientOnFocus: theme.mixins.blueGradient?.background as string,
    },
    {
      title: 'Fintech Software Development',
      description:
        'Looking for a skilled team to help you build an advanced fintech platform able to compete with the biggest in the game? At Rumble Fish, we’ve got what it takes to engineer innovative financial technology systems. We offer end-to-end fintech software development, consulting, and expertise.',
      linkTo: '/services/fintech-software-development/',
      icon: FintechIcon,
      gradientOnFocus: theme.mixins.blueGradient?.background as string,
    },
    {
      title: 'Dedicated Development Teams',
      description:
        'Our experts provide you with knowledge, skills, and experience that elevates every project to another level. We’ll gladly take ownership of the entire process and guide you and your team through the intricacies of cutting-edge technology development.',
      linkTo: '/services/dedicated-development-teams/',
      icon: TeamIcon,
      gradientOnFocus: theme.mixins.tealGradient?.background as string,
    },
    {
      title: 'Web Development',
      description:
        "If you’re in need of professional web development services, look no further! Rumble Fish's talented team has extensive experience in delivering top-tier web apps and websites with the use of battle-tested tools and technologies like React or Nest. We know just the right solutions to exceed your business requirements.",
      linkTo: '/services/web-development/',
      icon: WebIcon,
      gradientOnFocus: theme.mixins.raspberryGradient?.background as string,
    },
    {
      title: 'Mobile App Development',
      description:
        'Whether you need an Android, an IOS app, or both, the Rumble Fish team is here to help you deliver the beautiful and efficient mobile product that your customers will simply love to use! We craft fast and secure mobile apps with a wow factor to help our customers grow their businesses and reach their goals quicker.',
      linkTo: '/services/mobile-development/',
      icon: MobileIcon,
      gradientOnFocus: theme.mixins.magentaGradient?.background as string,
    },
    {
      title: 'IT services for your startup',
      description: `If you're looking for a team capable of turning your product concept into a beautiful and technologically intricate digital solution - look no further! Rumble Fish is your trusted software development partner ready to take you through the entire process of custom digital product creation - from the early stages of ideation to the post-launch support. Whether you're on a mission to build a mobile app, a Web3 product, or an advanced platform - we are here for you!`,
      linkTo: '/startup',
      icon: StartupIcon,
      gradientOnFocus: theme.mixins.greenGradient?.background as string,
    },
    {
      title: 'UI/UX',
      description:
        'We design sleek, intuitive, and highly effective interfaces to help you overcome your business challenges. After carefully evaluating and understanding your requirements we switch to the designing mode - the end goal is the beautiful digital solution that people love to use!',
      linkTo: '/services/product-design',
      icon: DesignIcon,
      gradientOnFocus: theme.mixins.aquamarineGradient?.background as string,
    },
  ];
};
